import { useChat } from '@ai-sdk/react'
import type { UIMessage } from 'ai'
import { useCallback, useEffect, useMemo, useRef, useState, type ChangeEvent, type FormEvent } from 'react'
import useAPI from '~/prix/react/hooks/api'
import resolveCurrentRequestToken from '~/prix/react/hooks/resolveCurrentRequestToken'

interface TextUIPart {
  type: 'text'
  text: string
}

type MessagePart = TextUIPart | { type: string; [key: string]: unknown }

interface ChatMessageMetadata {
  createdAt?: string
  costUsd?: number | null
  toolCalls?: any[]
}

type LLMChatMessage = UIMessage & {
  metadata?: ChatMessageMetadata
  toolCalls?: any[]
  costUsd?: number | null
  createdAt?: Date
}

interface UseLLMChatProps {
  chatId: number
  firstMessage?: string
  historyMessages?: any[]
  onResponse?: () => void
}

export default function useLLMChat({
  chatId,
  firstMessage,
  historyMessages,
  onResponse,
}: UseLLMChatProps) {
  const { context } = useAPI()
  const { bypassAuth, getToken, token: contextToken } = context
  const [input, setInput] = useState('')
  const isWaitingForFirstMessage = useRef<boolean>(true)

  const buildRequestBody = useCallback(
    ({
      id,
      messages,
      requestBody,
    }: {
      id: string
      messages: UIMessage[]
      requestBody?: object
    }) => {
      const lastMessage = (messages as LLMChatMessage[])[messages.length - 1]
      if (!lastMessage) {
        return {
          ...(requestBody ?? {}),
          chatId,
        }
      }

      const textParts = (lastMessage.parts ?? []).filter(
        (part: any) => part.type === 'text',
      ) as TextUIPart[]

      const textContent =
        textParts.length > 0
          ? textParts.map(part => part.text).join('')
          : (lastMessage.content ?? '')

      return {
        ...(requestBody ?? {}),
        chatId,
        message: {
          id: lastMessage.id ?? `${id}-${messages.length}`,
          role: 'user',
          content: textContent,
          createdAt: new Date().toISOString(),
        },
      }
    },
    [chatId],
  )

  const authenticatedFetch = useCallback<typeof fetch>(
    async (request, requestInit) => {
      const token = await resolveCurrentRequestToken({
        bypassAuth,
        getToken,
        token: contextToken,
      })

      const headers = new Headers(requestInit?.headers ?? undefined)
      if (token) {
        headers.set('Token', token)
      } else {
        headers.delete('Token')
      }

      return fetch(request, {
        ...requestInit,
        headers,
      })
    },
    [bypassAuth, contextToken, getToken],
  )

  /** Hook de chat do AI SDK (com streaming) */
  const chat = useChat({
    id: String(chatId),
    api: '/api/v1/llm-chat-message/create',
    fetch: authenticatedFetch,
    experimental_prepareRequestBody: buildRequestBody,
    onFinish() {
      onResponse?.()
    },
  })

  const { messages, setMessages, status, append } = chat

  const isLoading = status === 'submitted' || status === 'streaming'

  /* Atualiza as mensagens iniciais quando houver mudança de histórico */
  useEffect(() => {
    if (historyMessages && historyMessages.length > 0) {
      const mappedMessages: LLMChatMessage[] = historyMessages.map((historyMessage: any) => ({
        id: String(historyMessage.id),
        role: historyMessage.role,
        content: historyMessage.content ?? '',
        parts: historyMessage.content
          ? [
              {
                type: 'text',
                text: historyMessage.content,
              } as TextUIPart,
            ]
          : [],
        metadata: {
          createdAt: historyMessage.createdAt,
          costUsd: historyMessage.costUsd,
          toolCalls: historyMessage.toolCalls ?? [],
        },
        createdAt: historyMessage.createdAt ? new Date(historyMessage.createdAt) : undefined,
        costUsd: historyMessage.costUsd,
        toolCalls: historyMessage.toolCalls ?? [],
      }))

      setMessages(mappedMessages as any)
      isWaitingForFirstMessage.current = true
    } else {
      setMessages([] as any)
      isWaitingForFirstMessage.current = true
    }
  }, [historyMessages, chatId, setMessages])

  const handleInputChange = useCallback((event: ChangeEvent<HTMLTextAreaElement>) => {
    setInput(event.target.value)
  }, [])

  const handleSubmit = useCallback(
    (event: FormEvent<HTMLFormElement>) => {
      event.preventDefault()
      const text = input.trim()
      if (!text) return

      void append({
        role: 'user',
        content: text,
      } as any)
      setInput('')
    },
    [append, input],
  )

  /* Envia a primeira mensagem automaticamente se for um chat novo */
  useEffect(() => {
    if (!firstMessage) return
    if (historyMessages && historyMessages.length > 0) return
    if (!isWaitingForFirstMessage.current) return
    if (isLoading) return

    isWaitingForFirstMessage.current = false
    void append({
      role: 'user',
      content: firstMessage,
    } as any)
  }, [append, firstMessage, historyMessages, isLoading])

  /* Função para formatar custo salvo no banco (para mensagens históricas) */
  const formatSavedCost = useCallback((costUsd: number | null) => {
    if (!costUsd || costUsd <= 0) return null
    return `$${costUsd.toFixed(6)}`
  }, [])

  const sendQuickQuestion = useCallback(
    (text: string) => {
      const value = text.trim()
      if (!value) return
      if (isLoading) return
      void append({
        role: 'user',
        content: value,
      } as any)
    },
    [append, isLoading],
  )

  return {
    ...chat,
    input,
    setInput,
    handleInputChange,
    handleSubmit,
    isLoading,
    reload: chat.reload,
    sendQuickQuestion,
    lastUsage: null,
    formatCostFromUsage: () => null,
    formatSavedCost,
  }
}

export type { TextUIPart, MessagePart, ChatMessageMetadata, LLMChatMessage }
